/**
 * Staff → ProEpoxy Recipes admin page.
 *
 * List / create / edit / archive curated recipes. The dropdown at the top
 * of the Metallic Epoxy calculator (`RecipePicker`) is fed by this page.
 *
 * Pigment blends use compact sliders; save is blocked until percentages
 * sum to exactly 100 — matches the backend's validator so we never surface
 * a red API error for arithmetic the UI can prevent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, X, Sparkles, Palette, RefreshCcw, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METALLIC_COLORS = [
  'Gold', 'Bronze', 'Copper', 'Russet', 'Yellow', 'Silver', 'Grey',
  'Pearl', 'Ice White', 'Black', 'Red', 'Blue', 'Green', 'Purple', 'Beige',
];
const PRIMER_COLORS_BY_TYPE = {
  '1_part': ['White', 'Grey'],
  '2_part': ['White', 'Grey', 'Black'],
};
const TOP_COAT_FINISHES = {
  polyurethane: ['Gloss', 'Satin', 'Matt'],
  polyaspartic: ['Gloss'],
};

const EMPTY_RECIPE = {
  name: '',
  description: '',
  primer: { type: '1_part', color: 'White' },
  design_coat: { product_type: 'comfort' },
  pigments: [{ color: 'Silver', percent: 60 }, { color: 'Pearl', percent: 40 }],
  top_coat: { product_type: 'polyurethane', finish: 'Satin' },
  pigment_g_per_m2: 100,
};

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

async function fetchRecipes(includeArchived = false) {
  const res = await fetch(`${API_URL}/api/proepoxy/recipes?include_archived=${includeArchived ? 1 : 0}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function StaffProEpoxyRecipes() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | { ...recipe } | 'new'

  const reload = async () => {
    setLoading(true);
    try {
      const list = await fetchRecipes(showArchived);
      setRecipes(list);
    } catch (e) {
      toast.error('Could not load recipes', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showArchived]);

  const startNew = () => setEditing({ ...EMPTY_RECIPE, _mode: 'new' });
  const startEdit = (r) => setEditing({ ...r, _mode: 'edit' });
  const closeEdit = () => setEditing(null);

  const handleSaved = () => {
    closeEdit();
    reload();
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive "${r.name}"? Installers will no longer see it in the picker.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/proepoxy/recipes/${r.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Archived "${r.name}"`);
      reload();
    } catch (e) {
      toast.error('Archive failed', { description: String(e).slice(0, 200) });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-proepoxy-recipes-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg"
            data-testid="staff-recipes-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">ProEpoxy Recipe Catalogue</h1>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Curate signature looks that installers can load with one tap in the Metallic Epoxy calculator.
            </p>
          </div>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-3 py-2 text-sm font-semibold shrink-0"
            data-testid="staff-recipes-new-btn"
          >
            <Plus className="w-4 h-4" /> New recipe
          </button>
        </div>

        <div className="flex items-center gap-2 mb-3 text-xs text-gray-600">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="staff-recipes-toggle-archived"
            />
            Include archived
          </label>
          <button
            onClick={reload}
            className="ml-auto inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
            data-testid="staff-recipes-refresh-btn"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : recipes.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500 text-sm">
            No recipes yet. Click <strong>New recipe</strong> to add your first signature look.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recipes.map((r) => (
              <RecipeCard key={r.id} recipe={r} onEdit={() => startEdit(r)} onArchive={() => archive(r)} />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <RecipeEditorModal
          initial={editing}
          onClose={closeEdit}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function RecipeCard({ recipe, onEdit, onArchive }) {
  return (
    <div className={`bg-white rounded-xl border p-3 hover:shadow-sm transition-shadow ${recipe.is_active === false ? 'border-gray-200 opacity-70' : 'border-purple-200'}`} data-testid={`staff-recipe-card-${recipe.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900 truncate">{recipe.name}</h3>
            {recipe.is_active === false && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 inline-flex items-center gap-0.5">
                <EyeOff className="w-3 h-3" /> Archived
              </span>
            )}
          </div>
          {recipe.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{recipe.description}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="text-xs font-semibold text-purple-700 hover:text-purple-900 px-2 py-1" data-testid={`staff-recipe-edit-${recipe.id}`}>Edit</button>
          {recipe.is_active !== false && (
            <button onClick={onArchive} className="text-gray-400 hover:text-red-600 p-1" title="Archive" data-testid={`staff-recipe-archive-${recipe.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(recipe.pigments || []).map((p) => (
          <span key={p.color} className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full text-[10px] px-2 py-0.5 font-medium text-gray-700">
            <Palette className="w-2.5 h-2.5" /> {p.color} {p.percent}%
          </span>
        ))}
      </div>
      <div className="text-[10px] text-gray-500 mt-2 border-t pt-2">
        {recipe.primer?.type === '2_part' ? '2-part' : '1-part'} primer ({recipe.primer?.color || '—'}) ·{' '}
        {recipe.design_coat?.product_type === 'crystal' ? 'Crystal' : 'Comfort'} ·{' '}
        {recipe.top_coat?.product_type === 'polyaspartic' ? 'Polyaspartic' : 'Polyurethane'} {recipe.top_coat?.finish || '—'}
      </div>
    </div>
  );
}

function RecipeEditorModal({ initial, onClose, onSaved }) {
  const isNew = initial._mode === 'new';
  const [form, setForm] = useState(() => ({
    name: initial.name || '',
    description: initial.description || '',
    primer: initial.primer || { type: '1_part', color: 'White' },
    design_coat: initial.design_coat || { product_type: 'comfort' },
    pigments: (initial.pigments && initial.pigments.length ? initial.pigments : [{ color: 'Silver', percent: 100 }]).map((p) => ({ ...p })),
    top_coat: initial.top_coat || { product_type: 'polyurethane', finish: 'Satin' },
    pigment_g_per_m2: initial.pigment_g_per_m2 || 100,
  }));
  const [saving, setSaving] = useState(false);

  const totalPct = useMemo(
    () => Math.round(form.pigments.reduce((s, p) => s + (Number(p.percent) || 0), 0) * 100) / 100,
    [form.pigments]
  );

  const canSave = form.name.trim().length > 0 && totalPct === 100 && !saving;

  const setPrimerType = (type) => {
    const colors = PRIMER_COLORS_BY_TYPE[type] || ['White'];
    setForm((f) => ({
      ...f,
      primer: { type, color: colors.includes(f.primer.color) ? f.primer.color : colors[0] },
    }));
  };

  const setTopCoatType = (t) => {
    const finishes = TOP_COAT_FINISHES[t] || ['Gloss'];
    setForm((f) => ({
      ...f,
      top_coat: { product_type: t, finish: finishes.includes(f.top_coat.finish) ? f.top_coat.finish : finishes[0] },
    }));
  };

  const addPigment = () => {
    const used = new Set(form.pigments.map((p) => p.color));
    const next = METALLIC_COLORS.find((c) => !used.has(c)) || METALLIC_COLORS[0];
    setForm((f) => ({ ...f, pigments: [...f.pigments, { color: next, percent: 0 }] }));
  };

  const removePigment = (idx) => setForm((f) => ({ ...f, pigments: f.pigments.filter((_, i) => i !== idx) }));

  const updatePigment = (idx, patch) => {
    setForm((f) => ({
      ...f,
      pigments: f.pigments.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };

  const distributeEvenly = () => {
    const n = form.pigments.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    setForm((f) => ({
      ...f,
      pigments: f.pigments.map((p, i) => ({ ...p, percent: base + (i < remainder ? 1 : 0) })),
    }));
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const url = isNew
        ? `${API_URL}/api/proepoxy/recipes`
        : `${API_URL}/api/proepoxy/recipes/${initial.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      toast.success(isNew ? `Created "${form.name}"` : `Updated "${form.name}"`);
      onSaved();
    } catch (e) {
      toast.error('Save failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="staff-recipe-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="font-bold text-gray-900">{isNew ? 'New recipe' : `Edit recipe`}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="staff-recipe-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Signature Carrara"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
              data-testid="staff-recipe-editor-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description (optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Short one-liner installers see in the picker."
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
              data-testid="staff-recipe-editor-description"
            />
          </div>

          {/* Primer */}
          <fieldset className="border rounded-lg p-3">
            <legend className="text-xs font-bold text-gray-700 uppercase tracking-wider px-1">Ox Primer</legend>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-gray-600">
                Type
                <select
                  value={form.primer.type}
                  onChange={(e) => setPrimerType(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  data-testid="staff-recipe-editor-primer-type"
                >
                  <option value="1_part">1-Part Rapid Cure</option>
                  <option value="2_part">2-Part DPM Epoxy</option>
                </select>
              </label>
              <label className="text-xs font-medium text-gray-600">
                Colour
                <select
                  value={form.primer.color}
                  onChange={(e) => setForm((f) => ({ ...f, primer: { ...f.primer, color: e.target.value } }))}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  data-testid="staff-recipe-editor-primer-color"
                >
                  {(PRIMER_COLORS_BY_TYPE[form.primer.type] || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>

          {/* Design Coat */}
          <fieldset className="border rounded-lg p-3">
            <legend className="text-xs font-bold text-gray-700 uppercase tracking-wider px-1">Ox Design Coat</legend>
            <div className="flex gap-2">
              {[
                { id: 'comfort', label: 'Comfort Resin' },
                { id: 'crystal', label: 'Crystal Resin' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setForm((f) => ({ ...f, design_coat: { product_type: opt.id } }))}
                  className={`flex-1 text-sm font-semibold px-3 py-2 rounded-lg border transition ${
                    form.design_coat.product_type === opt.id
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                  }`}
                  data-testid={`staff-recipe-editor-design-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>

          {/* Pigments */}
          <fieldset className="border rounded-lg p-3">
            <legend className="text-xs font-bold text-gray-700 uppercase tracking-wider px-1">Pigment blend</legend>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-semibold ${totalPct === 100 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="staff-recipe-editor-total-percent">
                Total: {totalPct}% {totalPct === 100 ? '✓' : `(must be 100%)`}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={distributeEvenly}
                  className="text-[11px] font-semibold text-purple-700 hover:text-purple-900 px-2 py-1 rounded hover:bg-purple-50"
                  data-testid="staff-recipe-editor-distribute-even"
                >
                  Distribute evenly
                </button>
                <button
                  onClick={addPigment}
                  disabled={form.pigments.length >= METALLIC_COLORS.length}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 px-2 py-1 rounded"
                  data-testid="staff-recipe-editor-add-pigment"
                >
                  <Plus className="w-3 h-3" /> Add colour
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {form.pigments.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`staff-recipe-editor-pigment-${idx}`}>
                  <select
                    value={p.color}
                    onChange={(e) => updatePigment(idx, { color: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
                  >
                    {METALLIC_COLORS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={p.percent}
                    onChange={(e) => updatePigment(idx, { percent: Math.round(Number(e.target.value)) })}
                    className="flex-1 accent-purple-600"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={p.percent}
                    onChange={(e) => updatePigment(idx, { percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                  />
                  <span className="text-xs text-gray-500 w-4">%</span>
                  <button onClick={() => removePigment(idx)} className="text-gray-400 hover:text-red-600 p-1" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              Total metallic powder used per m² (default {form.pigment_g_per_m2}g). Colours are split by these percentages.
            </p>
          </fieldset>

          {/* Top Coat */}
          <fieldset className="border rounded-lg p-3">
            <legend className="text-xs font-bold text-gray-700 uppercase tracking-wider px-1">Top Coat</legend>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-gray-600">
                Product
                <select
                  value={form.top_coat.product_type}
                  onChange={(e) => setTopCoatType(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  data-testid="staff-recipe-editor-topcoat-type"
                >
                  <option value="polyurethane">Polyurethane</option>
                  <option value="polyaspartic">Polyaspartic</option>
                </select>
              </label>
              <label className="text-xs font-medium text-gray-600">
                Finish
                <select
                  value={form.top_coat.finish}
                  onChange={(e) => setForm((f) => ({ ...f, top_coat: { ...f.top_coat, finish: e.target.value } }))}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  data-testid="staff-recipe-editor-topcoat-finish"
                >
                  {(TOP_COAT_FINISHES[form.top_coat.product_type] || []).map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>
        </div>
        <div className="border-t px-4 py-3 flex justify-end gap-2 shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white text-sm font-semibold px-3 py-2 rounded-lg"
            data-testid="staff-recipe-editor-save"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : (isNew ? 'Create recipe' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}
